// Shared "colour by aspect" pill — the Palette-icon select consumed by BOTH
// the board HUD (`AspectControls`, where the empty option is "Type" = the
// default type colouring) and the view builder's `color` clause row (where the
// empty option is a "pick an aspect…" prompt and an empty pick is incomplete).
// Presentational: the host owns the key list + the selected value. Shares the
// HUD pill shape via `hudChipClass` (it wraps a native <select>, so it can't be
// a <HudChip> button — same shape, one style source).

import { Palette } from "lucide-react";
import { hudChipClass } from "../HudChip";

export interface ColorBySelectProps {
  readonly aspectKeys: readonly string[];
  /** Active colour key, or null = the empty option. */
  readonly value: string | null;
  readonly onChange: (key: string | null) => void;
  /** Label for the empty option (HUD: "Type"; builder: "pick an aspect…"). */
  readonly noneLabel?: string;
  /** Pill accent fill (HUD passes `!!value`; builder likewise). */
  readonly active?: boolean;
  readonly "aria-label"?: string;
  readonly title?: string;
  /** Forwarded to the label wrapper (e.g. the HUD's `data-testid`). */
  readonly "data-testid"?: string;
}

export function ColorBySelect({
  aspectKeys,
  value,
  onChange,
  noneLabel = "Type",
  active = value !== null,
  "aria-label": ariaLabel = "Colour by aspect",
  title = "Colour modules by an aspect (a string label key).",
  "data-testid": testid,
}: ColorBySelectProps) {
  return (
    <label
      title={title}
      data-testid={testid}
      className={hudChipClass({ active, accent: "violet", className: "cursor-pointer pr-2.5" })}
    >
      <Palette width={13} height={13} aria-hidden="true" className="shrink-0" />
      <select
        aria-label={ariaLabel}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="bg-transparent outline-hidden cursor-pointer text-inherit appearance-none pr-0"
      >
        <option value="" className="bg-slate-900 text-slate-200">
          {noneLabel}
        </option>
        {aspectKeys.map((k) => (
          <option key={k} value={k} className="bg-slate-900 text-slate-200">
            {k}
          </option>
        ))}
      </select>
      <span aria-hidden="true" className="opacity-60 -ml-0.5">
        ▾
      </span>
    </label>
  );
}
